import React from 'react';
import { Button, Checkbox, Icon, Modal } from '../../../../../shared/ui';
import { IconMap } from '../../../../../shared/sprite';
import { useDispatch, useSelector } from 'react-redux';
import { integrationsModel } from '../../../../../entities/integrations';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { boolean, object } from 'yup';
import { teamsModel } from '../../../../../features/teams';

type Props = {
  isOpen: boolean;
  onClose(): void;
};

type SalesforceSettingsSchema = {
  isPageOpenedSalesforceActivity: boolean;
  isPageSharedSalesforceActivity: boolean;
  isConversionEventsSalesforceActivity: boolean;
  isFileDownloadedSalesforceActivity: boolean;
  isFileUploadedSalesforceActivity: boolean;
  isVideoPlayedSalesforceActivity: boolean;
  isCallRecordedSalesforceActivity: boolean;
};

const validationSchema = object().shape({
  isPageOpenedSalesforceActivity: boolean().required(),
  isPageSharedSalesforceActivity: boolean().required(),
  isConversionEventsSalesforceActivity: boolean().required(),
  isFileDownloadedSalesforceActivity: boolean().required(),
  isFileUploadedSalesforceActivity: boolean().required(),
  isVideoPlayedSalesforceActivity: boolean().required(),
  isCallRecordedSalesforceActivity: boolean().required(),
});

export const SalesforceSettingsModal: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();

  const currentTeam = useSelector(
    teamsModel.selectors.selectCurrentTeamWithError
  );

  const isUpdatingSalesforceSettings = useSelector(
    integrationsModel.selectors.selectIsUpdatingSalesforceSettings
  );

  const { register, watch, handleSubmit } = useForm<SalesforceSettingsSchema>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      isPageOpenedSalesforceActivity:
        currentTeam.teamConfig.isPageOpenedSalesforceActivity,
      isPageSharedSalesforceActivity:
        currentTeam.teamConfig.isPageSharedSalesforceActivity,
      isConversionEventsSalesforceActivity:
        currentTeam.teamConfig.isConversionEventsSalesforceActivity,
      isCallRecordedSalesforceActivity:
        currentTeam.teamConfig.isCallRecordedSalesforceActivity,
      isFileDownloadedSalesforceActivity:
        currentTeam.teamConfig.isFileDownloadedSalesforceActivity,
      isFileUploadedSalesforceActivity:
        currentTeam.teamConfig.isFileUploadedSalesforceActivity,
      isVideoPlayedSalesforceActivity:
        currentTeam.teamConfig.isVideoPlayedSalesforceActivity,
    },
  });

  const onSubmit: SubmitHandler<SalesforceSettingsSchema> = (data) => {
    dispatch(
      integrationsModel.actions.updateSalesforceSettings({ data, cb: onClose })
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isShowCancelButton={false}
      className="w-120"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex items-start justify-between w-full">
          <Icon glyph={IconMap.Salesforce} width={48} />
          <Button
            color="transparent"
            variant="icon"
            onClick={onClose}
            size="sm"
          >
            <Icon glyph={IconMap.XClose} width={24} className="text-gray-400" />
          </Button>
        </div>

        <div className="text-display-xs font-display text-gray-900 font-medium mt-4 mb-5">
          Salesforce integration
        </div>
        <p className="text-sm text-gray-600">
          Select activity to sync with Salesforce:
        </p>
        <div className="flex flex-col gap-y-3 my-5">
          <Checkbox
            className="!items-start gap-2"
            {...register('isPageOpenedSalesforceActivity')}
            checked={watch('isPageOpenedSalesforceActivity')}
          >
            <p className="text-sm font-medium text-gray-700">
              When a page is opened
            </p>
          </Checkbox>
          <Checkbox
            className="!items-start gap-2"
            {...register('isPageSharedSalesforceActivity')}
            checked={watch('isPageSharedSalesforceActivity')}
          >
            <p className="text-sm font-medium text-gray-700">
              When a page is shared with a person
            </p>
          </Checkbox>
          <Checkbox
            className="!items-start gap-2"
            {...register('isConversionEventsSalesforceActivity')}
            checked={watch('isConversionEventsSalesforceActivity')}
          >
            <p className="text-sm font-medium text-gray-700">
              Conversion events (meeting booked, Link click, etc.)
            </p>
          </Checkbox>
          <Checkbox
            className="!items-start gap-2"
            {...register('isVideoPlayedSalesforceActivity')}
            checked={watch('isVideoPlayedSalesforceActivity')}
          >
            <p className="text-sm font-medium text-gray-700">
              When a video is played (video recording)
            </p>
          </Checkbox>
          <Checkbox
            className="!items-start gap-2"
            {...register('isCallRecordedSalesforceActivity')}
            checked={watch('isCallRecordedSalesforceActivity')}
          >
            <p className="text-sm font-medium text-gray-700 whitespace-nowrap">
              When a call is recorded and include call summary (call recording)
            </p>
          </Checkbox>
          {/* TODO: Add this back in once appropriate events are supported */}

          {/* <Checkbox
            className="!items-start gap-2"
            {...register('isFileDownloadedSalesforceActivity')}
            checked={watch('isFileDownloadedSalesforceActivity')}
          >
            <p className="text-sm font-medium text-gray-700">
              When a file is downloaded
            </p>
          </Checkbox>
          <Checkbox
            className="!items-start gap-2"
            {...register('isFileUploadedSalesforceActivity')}
            checked={watch('isFileUploadedSalesforceActivity')}
          >
            <p className="text-sm font-medium text-gray-700">
              When a file is uploaded
            </p>
          </Checkbox> */}
        </div>
        <Button
          color="primary"
          variant="text"
          className="w-34"
          type="submit"
          loading={isUpdatingSalesforceSettings}
        >
          Save settings
        </Button>
      </form>
    </Modal>
  );
};
